import { useEffect, DragEventHandler, useState, useRef } from "react";
import { ChangeAlertCallbackType, ChangeLoaderCallbackType } from "../../../Root";
import { closelightbox } from "../../../../util/Functions";
import { AfbService } from "../../../../services/AfbService";
import { CommonService } from "../../../../services/CommonService";
import { useDialog } from "@albo-ui/core";

export const usePreloadWorkers = (open: boolean, changeAlert: ChangeAlertCallbackType, changeLoader: ChangeLoaderCallbackType) => {
    const [isValidForm, setIsValidForm] = useState<boolean>(true);
    const [zPos, setZPos] = useState(45);
    const dialogProps = useDialog();
    const afbService = useRef<AfbService>();
    const commonService = useRef<CommonService>();

    const [data, setData] = useState([]);
    const [error, setError] = useState(null);
    const [form, setForm] = useState(null);
    const dropZoneRef = useRef<null | HTMLDivElement>(null);

    const saveData = async (result) => {
        changeLoader(true);
        setData(result);
        changeLoader(false);
    };

    const parseJson = (csv) => {
        changeLoader(true);
        setZPos(-1);
        setIsValidForm(true);
        setError(null);

        let f = form || {};
        let agent = localStorage.session ? JSON.parse(localStorage.session) : {};

        const reader = new FileReader();
        reader.onload = (e: any) => {
            const result = new Uint8Array(e.target.result);
            const workbook = window["XLSX"].read(result, { type: "array" });

            const firstSheetName = workbook.SheetNames[0];
            const worksheet = window["XLSX"].utils.sheet_to_json(
                workbook.Sheets[firstSheetName],
                { header: 1 }
            );

            const count = worksheet.filter((row) => row.length > 0).length;
            if (count > 1) {
                f["total_count"] = count - 1;
                f["file_name"] = csv.name;
                f["uuid_customer"] = data["uuid"];
                f["uuid_admin"] = agent["uuid"];

                const readerUrl = new FileReader();
                readerUrl.readAsDataURL(csv);
                readerUrl.onload = () => {
                    f["file_base_64"] = readerUrl.result;
                    setForm(JSON.parse(JSON.stringify(f)));
                };

                setForm(JSON.parse(JSON.stringify(f)));
                setIsValidForm(false);
            } else {
                setError("El archivo seleccionado está vacío");
                return;
            }
        };

        reader.readAsArrayBuffer(csv);
        changeLoader(false);
        setZPos(45);
    };

    const onSelectFile = (e) => {
        let csv = e.target.files[0];
        let extension = (name) => {
            return ["csv", "xls", "xlsx"].indexOf(name.split('.').pop()) === -1 ? true : false
        }

        if (!csv) {
            changeAlert(true, "error", "Error en selección de archivo");
        } else if (extension(csv.name)) {
            changeAlert(true, "error", "Error: El tipo de archivo no es permitido.");
        } else {
            parseJson(csv);
        }
    };

    const onDownloadFile = async () => {
        changeLoader(false);
        let path = "formats/workers.xlsx";

        if (path) {
            const request = await commonService.current.downloadFile(path);
            if (request.data.response === "OK") {
                window.open(request.data.content, "_blank");
            }
        }

        changeLoader(false);
    };

    const handleOver: DragEventHandler<HTMLDivElement> = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (dropZoneRef.current) {
            dropZoneRef.current.style.borderColor = "#1890ff";
            dropZoneRef.current.style.borderWidth = "2px";
            dropZoneRef.current.style.backgroundColor = "rgba(119, 119, 255,0.1)";
        }
    };

    const handleLeave: DragEventHandler<HTMLDivElement> = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (dropZoneRef.current) {
            dropZoneRef.current.style.borderColor = "lightgray";
            dropZoneRef.current.style.borderWidth = "1px";
            dropZoneRef.current.style.backgroundColor = "transparent";
        }
    };

    const handleDrop: DragEventHandler<HTMLDivElement> = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            const filesToUpload = [];
            for (let i = 0; i < event.dataTransfer.files.length; i++) {
                filesToUpload.push(event.dataTransfer.files.item(i));
            }

            if (filesToUpload.length > 0) {
                parseJson(filesToUpload[0]);
                handleLeave(event);
            }
            event.dataTransfer.clearData();
        }
    };

    const onClose = () => {
        dialogProps.onClose();
    };

    const onConfirm = async () => {
        changeLoader(true);
        setZPos(-1);
        
        const response = await afbService.current.afbPreloadWorkers({data: form});
        if (response?.data?.response === "OK") {
			changeAlert(true, 'success', "Preactivación enviada con éxito");
            changeLoader(false);
            closelightbox();
        } else {
			changeAlert(true, 'error', response["message"] || "Error al preactivar cuentas");
            changeLoader(false);
            setZPos(45);
        }
    };

    const onFormReset = () => {
        setForm(null);
        setError(null);
        setIsValidForm(true);
    };

    useEffect(() => {
        afbService.current = new AfbService(changeAlert);
        commonService.current = new CommonService(changeAlert);

        if (!open) {
            setData([]);
            setForm(null);
            setError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return {
        data,
        form,
        zPos,
        isValidForm,
        dropZoneRef,
        error,
        onSelectFile,
        onClose,
        onConfirm,
        handleOver,
        handleLeave,
        handleDrop,
        saveData,
        onFormReset,
        onDownloadFile
    };
};
